import Dexie, { type EntityTable } from 'dexie'

export interface LocalImage {
  id: string
  blob: Blob
  hash: string
  width: number
  height: number
  createdAt: number
}

const database = new Dexie('marlova-local-v1') as Dexie & {
  images: EntityTable<LocalImage, 'id'>
}

database.version(1).stores({
  images: 'id, createdAt, hash',
})

export async function storeImage(image: LocalImage): Promise<void> {
  await database.images.put(image)
}

export async function getImage(id: string): Promise<LocalImage | undefined> {
  return database.images.get(id)
}

export async function deleteImage(id: string): Promise<void> {
  await database.images.delete(id)
}

export async function clearImages(): Promise<void> {
  await database.images.clear()
}

export async function purgeExpiredImages(maxAgeMs = 24 * 60 * 60 * 1000): Promise<void> {
  const cutoff = Date.now() - maxAgeMs
  await database.images.where('createdAt').below(cutoff).delete()
}

async function decodeImage(file: File): Promise<ImageBitmap> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Select a JPEG, PNG, or WebP image.')
  }
  return createImageBitmap(file, { imageOrientation: 'from-image' })
}

export async function preprocessImage(file: File): Promise<LocalImage> {
  const image = await decodeImage(file)
  const scale = Math.min(1, 1600 / Math.max(image.width, image.height))
  const width = Math.max(1, Math.round(image.width * scale))
  const height = Math.max(1, Math.round(image.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('This browser could not process the image.')
  context.drawImage(image, 0, 0, width, height)
  image.close()
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Image encoding failed.')), 'image/jpeg', 0.82)
  })
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  const hash = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return { id: crypto.randomUUID(), blob, hash, width, height, createdAt: Date.now() }
}
